import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Heart, TrendingDown, Users, AlertTriangle, DollarSign,
  Activity, MapPin, RefreshCw, ChevronRight, Eye,
  BarChart2, Clock, Target, Zap
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminRetention() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [healthData, setHealthData] = useState({ customers: [], summary: {} });
  const [atRiskCustomers, setAtRiskCustomers] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState({});
  const [kpis, setKpis] = useState({});
  const [realtimeDashboard, setRealtimeDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [healthRes, riskRes, revenueRes, kpisRes, realtimeRes] = await Promise.all([
        fetch(`${API_URL}/api/retention/health/batch?limit=20`),
        fetch(`${API_URL}/api/retention/churn/at-risk?limit=10`),
        fetch(`${API_URL}/api/retention/revenue/summary?days=30`),
        fetch(`${API_URL}/api/retention/dashboard/kpis`),
        fetch(`${API_URL}/api/retention/dashboard/realtime`)
      ]);
      
      if (healthRes.ok) setHealthData(await healthRes.json());
      if (riskRes.ok) setAtRiskCustomers(await riskRes.json());
      if (revenueRes.ok) setRevenueSummary(await revenueRes.json());
      if (kpisRes.ok) setKpis(await kpisRes.json());
      if (realtimeRes.ok) setRealtimeDashboard(await realtimeRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const getHealthColor = (status) => {
    const colors = {
      'excellent': 'bg-green-500',
      'good': 'bg-blue-500',
      'fair': 'bg-yellow-500',
      'at_risk': 'bg-orange-500',
      'critical': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getHealthBadge = (status) => {
    const variants = {
      'excellent': 'default',
      'good': 'default',
      'fair': 'secondary',
      'at_risk': 'destructive',
      'critical': 'destructive'
    };
    const labels = {
      'excellent': 'Excelente',
      'good': 'Bueno',
      'fair': 'Regular',
      'at_risk': 'En Riesgo',
      'critical': 'Crítico'
    };
    return <Badge variant={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-retention">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Retención & Customer Health</h1>
              <p className="text-gray-600 mt-1">Health scores, churn prediction, revenue attribution</p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={fetchData} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
              <Button onClick={() => navigate('/admin')} variant="outline">
                Volver al Panel
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Tasa de Completado</p>
                  <p className="text-2xl font-bold">{kpis.warranties?.completion_rate || 0}%</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <Progress value={kpis.warranties?.completion_rate || 0} className="mt-2" />
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Satisfacción (NPS)</p>
                  <p className="text-2xl font-bold">{kpis.satisfaction?.nps || 0}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Heart className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="text-sm text-gray-500 mt-2">
                Rating: {kpis.satisfaction?.avg_rating || 0}⭐
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Ingresos (30d)</p>
                  <p className="text-2xl font-bold">{(kpis.revenue?.last_30_days || 0).toLocaleString('es-ES')}€</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Clientes en Riesgo</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {atRiskCustomers.total_count || 0}
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">
              <BarChart2 className="w-4 h-4 mr-2" />
              Resumen
            </TabsTrigger>
            <TabsTrigger value="health">
              <Heart className="w-4 h-4 mr-2" />
              Customer Health
            </TabsTrigger>
            <TabsTrigger value="churn">
              <TrendingDown className="w-4 h-4 mr-2" />
              Riesgo de Churn
            </TabsTrigger>
            <TabsTrigger value="revenue">
              <DollarSign className="w-4 h-4 mr-2" />
              Revenue
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Health Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Distribución de Customer Health</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(healthData.summary || {}).map(([status, count]) => (
                      <div key={status} className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${getHealthColor(status)}`} />
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="capitalize">{status.replace('_', ' ')}</span>
                            <span className="font-medium">{count}</span>
                          </div>
                          <Progress 
                            value={(count / (healthData.count || 1)) * 100} 
                            className="h-2"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Real-time Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    Actividad en Tiempo Real
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                          {realtimeDashboard.today?.new_warranties || 0}
                        </div>
                        <div className="text-sm text-gray-500">Garantías Hoy</div>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                          {realtimeDashboard.today?.completed || 0}
                        </div>
                        <div className="text-sm text-gray-500">Completadas Hoy</div>
                      </div>
                    </div>
                    
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Alertas Activas</h4>
                      <div className="flex gap-4">
                        <Badge variant="destructive">
                          {realtimeDashboard.alerts?.sla_alerts || 0} SLA
                        </Badge>
                        <Badge variant="secondary">
                          {realtimeDashboard.alerts?.at_risk_customers || 0} En riesgo
                        </Badge>
                      </div>
                    </div>

                    {/* Recent activity */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Actividad Reciente</h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {realtimeDashboard.recent_activity?.slice(0, 5).map((activity, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <Activity className="w-3 h-3 text-gray-400" />
                            <span className="truncate">{activity.product_name}</span>
                            <Badge variant="outline" className="text-xs ml-auto">
                              {activity.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Health Tab */}
          <TabsContent value="health">
            <Card>
              <CardHeader>
                <CardTitle>Customer Health Scores</CardTitle>
                <CardDescription>
                  Puntuación de salud basada en engagement, feedback, historial de compras y más
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {healthData.customers?.map((item, idx) => (
                    <div 
                      key={item.user?.user_id || idx}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${getHealthColor(item.health?.status)}`}>
                            {item.health?.health_score || 0}
                          </div>
                        </div>
                        <div>
                          <div className="font-medium">{item.user?.name || item.user?.email}</div>
                          <div className="text-sm text-gray-500">{item.user?.email}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {getHealthBadge(item.health?.status)}
                        
                        <div className="hidden md:flex gap-2">
                          {Object.entries(item.health?.components || {}).slice(0, 3).map(([key, value]) => (
                            <div key={key} className="text-center">
                              <div className="text-xs text-gray-500">{key.split('_')[0]}</div>
                              <div className="text-sm font-medium">{value}</div>
                            </div>
                          ))}
                        </div>
                        
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/admin/customer/${item.user?.user_id}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Churn Tab */}
          <TabsContent value="churn">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Clientes en Riesgo de Churn
                </CardTitle>
                <CardDescription>
                  Clientes identificados con alto riesgo de abandono que necesitan atención
                </CardDescription>
              </CardHeader>
              <CardContent>
                {atRiskCustomers.at_risk_customers?.length > 0 ? (
                  <div className="space-y-4">
                    {atRiskCustomers.at_risk_customers?.map((item, idx) => (
                      <div 
                        key={item.user?.user_id || idx}
                        className="p-4 border border-orange-200 rounded-lg bg-orange-50"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{item.user?.name || item.user?.email}</span>
                              <Badge variant="destructive">
                                Riesgo: {item.churn?.churn_risk_score}%
                              </Badge>
                            </div>
                            <div className="text-sm text-gray-500 mt-1">{item.user?.email}</div>
                            
                            {/* Risk factors */}
                            <div className="mt-3">
                              <div className="text-sm font-medium text-gray-700">Factores de riesgo:</div>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {item.churn?.risk_factors?.map((factor, fidx) => (
                                  <Badge key={fidx} variant="outline" className="text-xs">
                                    {factor.factor} (+{factor.points})
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            
                            {/* Recommendations */}
                            {item.churn?.recommendations?.length > 0 && (
                              <div className="mt-3">
                                <div className="text-sm font-medium text-gray-700">Recomendaciones:</div>
                                <ul className="text-sm text-gray-600 list-disc list-inside mt-1">
                                  {item.churn.recommendations.map((rec, ridx) => (
                                    <li key={ridx}>{rec}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                          
                          <Button variant="outline" size="sm">
                            Contactar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Heart className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>¡No hay clientes en riesgo alto de churn!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Resumen de Ingresos (30 días)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg text-white">
                      <div className="text-3xl font-bold">
                        {(revenueSummary.total_revenue || 0).toLocaleString('es-ES')}€
                      </div>
                      <div className="text-purple-100">Ingresos Totales</div>
                    </div>
                    <div className="text-center p-6 bg-gray-100 rounded-lg">
                      <div className="text-3xl font-bold text-gray-800">
                        {revenueSummary.total_invoices || 0}
                      </div>
                      <div className="text-gray-500">Facturas</div>
                    </div>
                    <div className="text-center p-6 bg-gray-100 rounded-lg">
                      <div className="text-3xl font-bold text-gray-800">
                        {(revenueSummary.average_order_value || 0).toLocaleString('es-ES')}€
                      </div>
                      <div className="text-gray-500">Ticket Medio</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Top Products */}
              <Card>
                <CardHeader>
                  <CardTitle>Productos Top por Ingresos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {revenueSummary.top_products?.map((product, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                            {idx + 1}
                          </div>
                          <span className="font-medium">{product.product}</span>
                        </div>
                        <span className="font-bold text-green-600">
                          {product.revenue.toLocaleString('es-ES')}€
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default AdminRetention;
